/**
 * ComplaintsRegister (C-2) — market/customer complaint register.
 * KPI cards + status/criticality filters + table + "Log complaint" dialog.
 * Route: /complaints
 */
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { PageHero } from '@edms/design-system';
import styles from './Complaints.module.css';
import {
  useComplaintsQuery, useCreateComplaint, CATEGORY_LABEL, STATUS_LABEL, RECALL_LABEL,
  type NewComplaintInput,
} from './useComplaints';

function fmtDate(iso: string | null | undefined) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: '2-digit' }); }
  catch { return String(iso); }
}
function critClass(c: string) {
  return c === 'CRITICAL' ? styles.critCritical : c === 'MAJOR' ? styles.critMajor : styles.critMinor;
}

export function ComplaintsRegister() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [criticality, setCriticality] = useState('');
  const q = useComplaintsQuery({ status: status || undefined, criticality: criticality || undefined });
  const create = useCreateComplaint();

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<NewComplaintInput>({ productName: '', description: '', criticality: 'MINOR', category: 'QUALITY_DEFECT' });
  const [err, setErr] = useState<string | null>(null);

  const items = q.data?.items ?? [];
  const stats = q.data?.stats;

  async function submit() {
    setErr(null);
    if (!form.productName.trim() || !form.description.trim()) { setErr('Product and description are required.'); return; }
    try {
      const created = await create.mutateAsync(form);
      setOpen(false);
      setForm({ productName: '', description: '', criticality: 'MINOR', category: 'QUALITY_DEFECT' });
      navigate({ to: '/complaints/$id', params: { id: created.complaintId } });
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'Could not log the complaint.');
    }
  }

  return (
    <main className={styles.page}>
      <PageHero
        eyebrow="Quality Events"
        title="Complaints"
        subtitle="Market & customer complaints — log, investigate, assess recall risk, link to CAPA, and close."
        actions={
          <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => { setOpen(true); setErr(null); }}>
            Log complaint
          </button>
        }
      />

      {stats && (
        <div className={styles.kpiRow}>
          <div className={styles.kpi}><span className={styles.kpiNum}>{stats.open}</span><span className={styles.kpiLabel}>Open</span></div>
          <div className={`${styles.kpi} ${stats.critical_open > 0 ? styles.kpiAlert : ''}`}><span className={styles.kpiNum}>{stats.critical_open}</span><span className={styles.kpiLabel}>Critical open</span></div>
          <div className={`${styles.kpi} ${stats.adverse_open > 0 ? styles.kpiAlert : ''}`}><span className={styles.kpiNum}>{stats.adverse_open}</span><span className={styles.kpiLabel}>Adverse events</span></div>
          <div className={`${styles.kpi} ${stats.recall_flagged > 0 ? styles.kpiWarn : ''}`}><span className={styles.kpiNum}>{stats.recall_flagged}</span><span className={styles.kpiLabel}>Recall flagged</span></div>
          <div className={`${styles.kpi} ${stats.overdue > 0 ? styles.kpiWarn : ''}`}><span className={styles.kpiNum}>{stats.overdue}</span><span className={styles.kpiLabel}>Overdue</span></div>
        </div>
      )}

      <div className={styles.filters}>
        <select aria-label="Status filter" value={status} onChange={(e) => setStatus(e.target.value)} className={styles.select}>
          <option value="">All statuses</option>
          {Object.keys(STATUS_LABEL).map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
        </select>
        <select aria-label="Criticality filter" value={criticality} onChange={(e) => setCriticality(e.target.value)} className={styles.select}>
          <option value="">All criticalities</option>
          <option value="CRITICAL">Critical</option>
          <option value="MAJOR">Major</option>
          <option value="MINOR">Minor</option>
        </select>
        <span className={styles.resultCount}>{items.length} complaint{items.length === 1 ? '' : 's'}</span>
      </div>

      {q.isLoading ? (
        <div className={styles.empty}>Loading complaints…</div>
      ) : items.length === 0 ? (
        <div className={styles.empty}>No complaints match the current filters.</div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Complaint</th><th>Product / batch</th><th>Category</th><th>Criticality</th>
              <th>Recall</th><th>Status</th><th>Received</th><th>Due</th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.complaintId} className={styles.row} onClick={() => navigate({ to: '/complaints/$id', params: { id: c.complaintId } })}>
                <td className={styles.mono}>{c.complaintNumber}{c.isAdverseEvent && <span className={styles.aeTag} title="Adverse event">AE</span>}</td>
                <td>{c.productName}{c.batchNumber ? <span className={styles.sub}> · {c.batchNumber}</span> : ''}</td>
                <td>{CATEGORY_LABEL[c.category] ?? c.category}</td>
                <td><span className={`${styles.pill} ${critClass(c.criticality)}`}>{c.criticality}</span></td>
                <td>{RECALL_LABEL[c.recallAssessment] ?? c.recallAssessment}</td>
                <td><span className={styles.statusPill}>{STATUS_LABEL[c.status] ?? c.status}</span></td>
                <td>{fmtDate(c.receivedDate)}</td>
                <td>{fmtDate(c.dueDate)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {open && (
        <div className={styles.modalBackdrop} role="dialog" aria-modal aria-label="Log complaint">
          <div className={styles.modal}>
            <h2 className={styles.modalTitle}>Log a complaint</h2>
            {err && <div className={styles.err} role="alert">{err}</div>}
            <label className={styles.field}><span>Product *</span>
              <input type="text" value={form.productName} onChange={(e) => setForm({ ...form, productName: e.target.value })} placeholder="e.g. Paracetamol 500mg Tablets" /></label>
            <div className={styles.field2}>
              <label className={styles.field}><span>Batch #</span>
                <input type="text" value={form.batchNumber ?? ''} onChange={(e) => setForm({ ...form, batchNumber: e.target.value })} /></label>
              <label className={styles.field}><span>Market</span>
                <input type="text" value={form.market ?? ''} onChange={(e) => setForm({ ...form, market: e.target.value })} placeholder="Saudi Arabia" /></label>
            </div>
            <label className={styles.field}><span>Complainant</span>
              <input type="text" value={form.complainant ?? ''} onChange={(e) => setForm({ ...form, complainant: e.target.value })} placeholder="Name / Anonymous" /></label>
            <div className={styles.field2}>
              <label className={styles.field}><span>Category</span>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  {Object.keys(CATEGORY_LABEL).map((k) => <option key={k} value={k}>{CATEGORY_LABEL[k]}</option>)}
                </select></label>
              <label className={styles.field}><span>Criticality</span>
                <select value={form.criticality} onChange={(e) => setForm({ ...form, criticality: e.target.value })}>
                  <option value="MINOR">Minor</option><option value="MAJOR">Major</option><option value="CRITICAL">Critical</option>
                </select></label>
            </div>
            <label className={styles.fieldCheck}>
              <input type="checkbox" checked={!!form.isAdverseEvent} onChange={(e) => setForm({ ...form, isAdverseEvent: e.target.checked })} />
              <span>Adverse event (opens straight into Medical review)</span>
            </label>
            <label className={styles.field}><span>Description *</span>
              <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
            <div className={styles.modalActions}>
              <button type="button" className={`${styles.btn} ${styles.btnNeutral}`} onClick={() => setOpen(false)}>Cancel</button>
              <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => void submit()} disabled={create.isPending}>
                {create.isPending ? 'Logging…' : 'Log complaint'}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
